/**
 * @file SearchPayments.js
 * @version 1.6.6
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-07-27
 * @modified 2026-10-01
 *
 * Standalone payments search — the "Payments" tab of the Credit & Payments
 * page. Lists payments.payments via GET /api/payments/search (server-side
 * filtering, sorting and pagination). Row → ContactModal for the payer.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { API_URL } from '../config';
import './SearchLayout.css';
import './TableStyles.css';
import ReactDOM from 'react-dom';
import { showAlert } from './Alert';
import ContactModal from './ContactModal';
import InvoiceModal from './InvoiceModal';
import BillModal from './BillModal';
import LoadingSpinner from './LoadingSpinner';
import PaginationControls from './PaginationControls';
import useColumnFilters from '../hooks/useColumnFilters';
import ColumnFilterMenu from './ColumnFilterMenu';
import ColumnFilterNote from './ColumnFilterNote';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import DateInput from './DateInput';
import Dropdown from './Dropdown';
import { useSlideOutButton } from './SlideOutButton';
import usePersistedState from '../hooks/usePersistedState';
import { hasPermission } from '../utils/permissions';
import AccessDenied from './AccessDenied';
import { formatCurrency } from '../utils/currencyFormatter';
import { formatDateShort } from '../utils/dateFormatters';
import { paymentNature } from '../utils/paymentTypes';
import './SearchPayments.css';
import EntityLink from './EntityLink';
import SearchHelp from './SearchHelp';
import useLatestRequest from '../hooks/useLatestRequest';
import { useReveal, revealFilters } from '../utils/revealInSearch';
import { onEnterKey } from '../utils/enterKey';

// Also what Clear Filters resets to, and so what "no filters applied" means
// for the button's highlight and the toolbar pill (utils/activeFilters.js).
const DEFAULT_FILTERS = {
  search: '',
  dateFrom: '',
  dateTo: '',
  status: '',
  is_reconciled: '',
  limit: 50
};

function SearchPayments({ user }) {
  useSlideOutButton();
  // Arriving from a modal's "Find in …" (utils/revealInSearch.js): the term
  // seeds the filters and the page goes back to 1 in the FIRST render's state.
  const reveal = useReveal('search-payments');
  const [filters, setFilters] = usePersistedState('searchPayments.filters', DEFAULT_FILTERS,
    { seed: revealFilters(reveal) });
  const [page, setPage] = usePersistedState('searchPayments.page', 1, { seed: reveal ? 1 : null });
  const [sortField, setSortField] = usePersistedState('searchPayments.sortField', 'payment_date');
  const [sortDirection, setSortDirection] = usePersistedState('searchPayments.sortDirection', 'desc');
  const [selectedContactId, setSelectedContactId] = usePersistedState('searchPayments.contactModalId', null);
  const [viewInvoiceId, setViewInvoiceId] = usePersistedState('searchPayments.invoiceModalId', null);
  const [viewBillId, setViewBillId] = usePersistedState('searchPayments.billModalId', null);

  const [payments, setPayments] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const permitted = hasPermission(user, 'credit_control');

  // Typing fires a search per pause and the responses can land out of order —
  // only the newest may write the table.
  const latest = useLatestRequest();

  const fetchPayments = useCallback(async () => {
    const seq = latest.begin();
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.set('search', filters.search);
      if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.set('dateTo', filters.dateTo);
      if (filters.status) params.set('status', filters.status);
      if (filters.is_reconciled) params.set('is_reconciled', filters.is_reconciled);
      params.set('page', page);
      params.set('limit', filters.limit);
      params.set('sortField', sortField);
      params.set('sortDirection', sortDirection);

      const res = await fetch(`${API_URL}/api/payments/search?${params.toString()}`, { credentials: 'include' });
      const data = await res.json();
      if (!latest.isLatest(seq)) return;   // superseded by a later search
      if (!res.ok) throw new Error(data.error || 'Failed to load payments');
      const total = data.total || 0;
      const limit = data.limit || filters.limit;
      setPayments(data.payments || []);
      setPagination({ page: data.page || page, limit, total, totalPages: Math.max(1, Math.ceil(total / limit)) });
    } catch (e) {
      if (!latest.isLatest(seq)) return;
      showAlert('Failed to load payments: ' + e.message);
    } finally {
      if (latest.isLatest(seq)) setLoading(false);
    }
  }, [filters, page, sortField, sortDirection, latest]);

  // Debounce so typing in the search box doesn't fire a request per keystroke.
  useEffect(() => {
    if (!permitted) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchPayments, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [fetchPayments, permitted]);

  // Right-click a header to narrow by the values on this page.
  const cols = useColumnFilters(payments, {
    contact: { label: 'Contact', value: (p) => p.contact_name },
    method: { label: 'Method', value: (p) => p.payment_method },
    account: { label: 'Account', value: (p) => p.account_name || p.account_code },
    reconciled: { label: 'Reconciled', value: (p) => (p.is_reconciled ? 'Reconciled' : 'Unreconciled') }
  });

  if (!permitted) {
    return <AccessDenied permission="Credit Control" />;
  }

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setPage(1);
  };

  const sortArrow = (field) => sortField === field ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : '';

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSortField('payment_date');
    setSortDirection('desc');
    // Column filters are filters too; the button says it clears all of them.
    cols.clearEverything();
    setPage(1);
  };

  // Any filter at all — header or column — lights the Clear Filters button.
  const headerFilters = activeHeaderFilters(filters, DEFAULT_FILTERS, { labels: { is_reconciled: 'Reconciled' } });
  const filtersApplied = headerFilters.count > 0 || cols.activeCount > 0;

  return (
    <div className="search-payments">
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Search customer receipts and supplier payments</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span style={{ margin: 'auto 0' }}>Payments sync from Xero hourly (read-only).</span>
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
            <button
              className={clearFiltersClass(filtersApplied)}
              onClick={clearFilters}
              title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
            >
              <div className="icon-wrapper"><span className="material-symbols-rounded">filter_alt_off</span></div>
              <span className="btn-label">Clear Filters</span>
            </button>
            <button className="btn-slideout" onClick={fetchPayments} disabled={loading}>
              <div className="icon-wrapper"><span className={`material-symbols-rounded ${loading ? 'spinning' : ''}`}>sync</span></div>
              <span className="btn-label">Refresh</span>
            </button>
          </div>
          <div className="search-stats" style={{ gridColumn: '1 / 3' }}>
            {pagination.total.toLocaleString('en-GB')} payment{pagination.total === 1 ? '' : 's'}
          </div>
        </div>
      </div>

      <div className="filter-row" style={{ gridTemplateColumns: 'auto 100px 100px 150px 100px' }}>
        <div className="filter-group">
          <label>
            Search
            <SearchHelp
              amounts
              fields={['reference', 'contact', 'account no.', 'amount']}
            />
          </label>
          <input
            type="text"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            onKeyDown={onEnterKey(() => {
              // Enter searches now rather than after the typing pause.
              if (debounceRef.current) clearTimeout(debounceRef.current);
              fetchPayments();
            })}
            placeholder="Reference, contact or account no…"
          />
        </div>
        <div className="filter-group">
          <DateInput label="From Date" value={filters.dateFrom} onChange={(e) => handleFilterChange('dateFrom', e.target.value)} />
        </div>
        <div className="filter-group">
          <DateInput label="To Date" value={filters.dateTo} onChange={(e) => handleFilterChange('dateTo', e.target.value)} />
        </div>
        <div className="filter-group">
          <label>Reconciled</label>
          <Dropdown
            value={filters.is_reconciled}
            onChange={(value) => handleFilterChange('is_reconciled', value)}
            options={[
              { value: '', label: 'Any' },
              { value: 'true', label: 'Reconciled' },
              { value: 'false', label: 'Unreconciled' }
            ]}
          />
        </div>
        <div className="filter-group">
          <label>Per Page</label>
          <Dropdown
            value={filters.limit}
            onChange={(value) => handleFilterChange('limit', parseInt(value))}
            options={[25, 50, 100, 200].map(n => ({ value: n, label: `${n}` }))}
          />
        </div>
      </div>

      <div className="table-toolbar payments-pagination">
        <ColumnFilterNote cols={cols} header={headerFilters} onClear={clearFilters} />
        <PaginationControls pagination={pagination} onPageChange={setPage} />
      </div>

      <div className="table-container">
        <table className="data-table table-min-lg">
          <thead>
            <tr>
              <th className="sortable" onClick={() => handleSort('payment_date')}>Date{sortArrow('payment_date')}</th>
              <th onContextMenu={cols.headerProps('contact').onContextMenu}
                  className={cols.isFiltered('contact') ? 'col-filtered' : undefined}
                  title="Right-click to filter by the contacts on this page">Contact</th>
              <th onContextMenu={cols.headerProps('method').onContextMenu}
                  className={cols.isFiltered('method') ? 'col-filtered' : undefined}
                  title="Right-click to filter by the methods on this page">Method</th>
              <th onContextMenu={cols.headerProps('account').onContextMenu}
                  className={cols.isFiltered('account') ? 'col-filtered' : undefined}
                  title="Right-click to filter by the accounts on this page">Account</th>
              <th className="sortable" onClick={() => handleSort('reference')}>Reference{sortArrow('reference')}</th>
              <th>Invoice / Bill</th>
              <th onContextMenu={cols.headerProps('reconciled').onContextMenu}
                  className={`text-center ${cols.isFiltered('reconciled') ? 'col-filtered' : ''}`.trim()}
                  title="Right-click to filter by reconciled state on this page">Reconciled</th>
              <th className="sortable num" style={{ textAlign: 'right' }} onClick={() => handleSort('amount')}>Amount{sortArrow('amount')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="8"><LoadingSpinner /></td></tr>
            ) : cols.rows.length === 0 ? (
              <tr><td colSpan="8" className="no-results">No payments match these filters.</td></tr>
            ) : (
              cols.rows.map((p) => (
                <tr key={p.id}>
                  <td>{formatDateShort(p.payment_date)}</td>
                  <td>
                    {p.account_id ? (
                      <span className="name-with-badge">
                        {p.contact_account_no && (
                          <span
                            className="account-badge-inline"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => { e.stopPropagation(); setSelectedContactId(p.account_id); }}
                          >
                            {p.contact_account_no}
                          </span>
                        )}
                        <span className="contact-link" onClick={() => setSelectedContactId(p.account_id)}>
                          {p.contact_name || `Account ${p.account_id}`}
                        </span>
                      </span>
                    ) : (
                      <span className="pb-muted">—</span>
                    )}
                  </td>
                  <td>{paymentNature(p)}</td>
                  <td>
                    <EntityLink type="payment" id={p.id}>{p.bank_account_name || 'Payment'}</EntityLink>
                  </td>
                  <td>{p.reference || '—'}</td>
                  <td>
                    {p.invoice_no && p.invoice_id ? (
                      <span className="contact-link" onClick={() => setViewInvoiceId(p.invoice_id)}>{p.invoice_no}</span>
                    ) : p.bill_number && p.bill_id ? (
                      <span className="contact-link" onClick={() => setViewBillId(p.bill_id)}>{p.bill_number}</span>
                    ) : (p.invoice_no || p.bill_number || '—')}
                  </td>
                  <td className="text-center">
                    {p.is_reconciled
                      ? <span className="material-symbols-rounded" style={{ color: 'var(--color-success)' }}>check_circle</span>
                      : <span className="material-symbols-rounded" style={{ color: 'var(--text-muted)' }}>radio_button_unchecked</span>}
                  </td>
                  <td className="num" style={{ textAlign: 'right' }}>{formatCurrency(p.amount)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
            
      {selectedContactId && ReactDOM.createPortal(
        <ContactModal
          contactId={selectedContactId}
          onClose={() => setSelectedContactId(null)}
        />,
        document.body
      )}

      {viewInvoiceId && ReactDOM.createPortal(
        <InvoiceModal invoiceId={viewInvoiceId} onClose={() => { setViewInvoiceId(null); fetchPayments(); }} />,
        document.body
      )}

      {viewBillId && ReactDOM.createPortal(
        <BillModal billId={viewBillId} onClose={() => { setViewBillId(null); fetchPayments(); }} />,
        document.body
      )}
      <ColumnFilterMenu cols={cols} />
    </div>
  );
}

export default SearchPayments;
